import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { EnvelopeSimple, Phone, MapPin, PaperPlaneTilt, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import PhoneInput from '@/components/PhoneInput';

import { API } from '@/config/api';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', phone_code: '+1', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, phone: form.phone ? `${form.phone_code} ${form.phone}` : '' });
      setSent(true);
      toast.success('Message sent successfully!');
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-blue-600 font-medium text-sm uppercase tracking-widest mb-3">Contact Us</p>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight mb-6">Let's Talk About Your Future</h1>
            <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
              Whether you're a student looking for guidance or a recruiter interested in partnering with us, we'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-4 space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6" style={{ fontFamily: "'Libre Baskerville', serif" }}>Get in Touch</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                      <EnvelopeSimple size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Email</p>
                      <p className="text-sm text-gray-500">hello@studyalliance.ca</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                      <Phone size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Phone</p>
                      <p className="text-sm text-gray-500">+1 (604) 735-9791</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                      <MapPin size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Location</p>
                      <p className="text-sm text-gray-500">2025 Willingdon Ave, Suite 900<br/>Burnaby, BC V5C 0J3</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-6 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-sm font-semibold text-gray-900 mb-2">Office Hours</p>
                <p className="text-sm text-gray-500">Monday - Friday: 9:00 AM - 6:00 PM PST</p>
                <p className="text-sm text-gray-500">Saturday: 10:00 AM - 2:00 PM PST</p>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-8">
              {sent ? (
                <div className="text-center py-16" data-testid="contact-success">
                  <CheckCircle size={48} weight="fill" className="text-green-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Libre Baskerville', serif" }}>Message Sent!</h3>
                  <p className="text-gray-500">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                  <Button onClick={() => { setSent(false); setForm({ name: '', email: '', phone: '', phone_code: '+1', subject: '', message: '' }); }} variant="outline" className="mt-6" data-testid="send-another-btn">
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input id="name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="contact-name-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input id="email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required data-testid="contact-email-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <PhoneInput
                        id="phone"
                        value={form.phone}
                        onChange={e => setForm({ ...form, phone: e.target.value })}
                        countryCode={form.phone_code}
                        onCountryCodeChange={v => setForm({ ...form, phone_code: v })}
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject">Subject</Label>
                      <Input id="subject" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea id="message" rows={5} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} required data-testid="contact-message-input" />
                  </div>
                  <Button type="submit" disabled={loading} className="bg-blue-800 hover:bg-blue-700 text-white" data-testid="contact-submit-btn">
                    <PaperPlaneTilt size={18} className="mr-2" />
                    {loading ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
